@let header = headerStore.header;
@let hasChanges = hasUnsavedChanges(headerForm);
@if (headerStore.isLoading()) {
  <div class="pt-32">
    <aw-loading-animation></aw-loading-animation>
  </div>
}
@if (header() && !headerStore.isLoading()) {
  <div [formGroup]="languageForm" class="flex w-full justify-end">
    <aw-language-switcher
      formControlName="languageSelect"
      [label]="cmsTextProvider.shared.languageSelection"
      id="language-select">
    </aw-language-switcher>
  </div>

  <aw-section [formGroup]="headerForm" testIdAffix="header">
    <aw-section-header>
      <aw-section-headline>
        {{ cmsTextProvider.shared.header }}
      </aw-section-headline>
      <aw-section-header-actions>
        <aw-reveal-form-errors-button
          [form]="headerForm"></aw-reveal-form-errors-button>
        <aw-button
          [id]="'save-entity'"
          [style]="'primary'"
          (click)="save()"
          [type]="'submit'"
          [isDisabled]="!headerForm.valid || !hasChanges"
          [text]="cmsTextProvider.shared.save"></aw-button>
        <aw-button
          [id]="'discard-changes'"
          [style]="'primary-alt'"
          [type]="'reset'"
          [isDisabled]="!hasChanges"
          (click)="handleAbortChangesDialog()"
          [text]="cmsTextProvider.shared.cancel"></aw-button>
      </aw-section-header-actions>
    </aw-section-header>

    <!-- Accessibility Hint-->
    <aw-accessibility-banner>
      <p>
        {{ cmsTextProvider.shared.accessibilityHeadline }}
      </p>
      <ul class="list-disc pl-5">
        @for (hint of cmsTextProvider.shared.accessibilityHints; track $index) {
          <li>{{ hint }}</li>
        }
      </ul>
    </aw-accessibility-banner>

    <aw-section testIdAffix="colors">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.shared.colours }}
        </aw-section-headline>
      </aw-section-header>
      <span>
        {{ cmsTextProvider.header.backgroundColourExplanation }}
      </span>
      <div class="flex w-full items-start justify-end gap-7">
        <aw-color-picker-input
          formControlName="fontColor"
          [label]="cmsTextProvider.shared.fontColour"
          id="fontColor" />
        <aw-button-group
          id="fontColorButtonGroup"
          [options]="fontColors | enumToButtonOptionArray"
          [formControl]="fontColorBtn"
          (valueChange)="onFontColorChange($event)" />
      </div>
      <div class="flex w-full items-start justify-end gap-7">
        <aw-color-picker-input
          formControlName="backgroundColor"
          [label]="cmsTextProvider.shared.backgroundColour"
          id="backgroundColor" />
        <aw-button-group
          id="backgroundColorButtonGroup"
          [options]="fontColors | enumToButtonOptionArray"
          [formControl]="backgroundColorBtn"
          (valueChange)="onBackgroundColorChange($event)" />
      </div>
    </aw-section>

    <aw-section testIdAffix="landingPageButton">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.header.button }}
        </aw-section-headline>
      </aw-section-header>
      <span>
        {{ cmsTextProvider.header.headerImageExplanation }}
      </span>
      <div class="flex w-full items-center justify-end gap-6">
        <aw-translation-text-input
          [entityType]="EntityType.header"
          [attr.scroll-mark]="headerForm.value.headlines?.[0]?.id"
          [relationType]="TextRelationType.headline"
          formControlName="headlines"
          [label]="cmsTextProvider.shared.titleOptional"
          id="headlines">
        </aw-translation-text-input>
      </div>
      <div class="flex w-full items-center justify-end gap-6">
        <div class="flex w-full gap-6">
          <aw-image-select
            class="w-full"
            [persistUsageOnSelect]="true"
            [usageLabel]="'header.logoImage'"
            [id]="'logoImage'"
            [formControl]="headerForm.controls.logo.controls.image"
            [label]="cmsTextProvider.shared.image" />
          <aw-translation-accessibility-text-input
            [entityType]="EntityType.header"
            [attr.scroll-mark]="
              headerForm.controls.logo.value.imageAlts?.[0]?.id
            "
            [relationType]="TextRelationType.imageAlt"
            class="w-full"
            [id]="'logoText'"
            [formControl]="headerForm.controls.logo.controls.imageAlts"
            [label]="cmsTextProvider.shared.altText" />
        </div>
      </div>
      <div class="flex w-full items-center justify-end gap-7">
        <aw-number-input
          [formControl]="headerForm.controls.headlineFontSize"
          [label]="cmsTextProvider.header.fontSize"
          id="headlineFontSize" />
        <aw-button-group
          id="headlineFontSizeButtonGroup"
          [options]="fontSizes | enumToButtonOptionArray"
          [formControl]="headlineFontSizeBtn"
          (valueChange)="onHeadlinesFontSizeChange($event)" />
      </div>
      <div class="flex flex-row">
        <span [style.fontSize.pt]="headerForm!.controls.headlineFontSize.value">
          {{ cmsTextProvider.header.preview }}
        </span>
      </div>
    </aw-section>

    <aw-section testIdAffix="images">
      <aw-section-header>
        <aw-section-headline>
          {{ cmsTextProvider.header.additionalImages }}
        </aw-section-headline>
      </aw-section-header>
      <span>
        {{ cmsTextProvider.header.additionalImagesInfo }}
      </span>
      <div class="flex w-full items-center justify-end gap-6">
        <div class="flex w-full gap-6">
          <aw-image-select
            class="w-full"
            [usageLabel]="'header.leftImage'"
            [id]="'ImageLeft'"
            [persistUsageOnSelect]="true"
            [formControl]="headerForm.controls.leftImage.controls.image"
            [label]="cmsTextProvider.header.imageLeft" />
          <aw-translation-accessibility-text-input
            [entityType]="EntityType.header"
            [attr.scroll-mark]="headerForm.controls.leftImage.value.id"
            [relationType]="TextRelationType.imageAlt"
            class="w-full"
            [id]="'ImageLeftText'"
            [formControl]="headerForm.controls.leftImage.controls.imageAlts"
            [label]="cmsTextProvider.header.altTextLeft" />
          <aw-button
            [id]="'delete-left-image-icon'"
            [type]="'button'"
            (click)="deleteImageLeft()"
            [style]="'error'"
            class="w-12">
            <span class="material-symbols-rounded"> delete </span>
          </aw-button>
        </div>
      </div>
      <div class="flex w-full items-center justify-end gap-6">
        <div class="flex w-full gap-6">
          <aw-image-select
            class="w-full"
            [usageLabel]="'header.rightImage'"
            [id]="'ImageRight'"
            [persistUsageOnSelect]="true"
            [formControl]="headerForm.controls.rightImage.controls.image"
            [label]="cmsTextProvider.header.imageRight" />
          <aw-translation-accessibility-text-input
            [entityType]="EntityType.header"
            [relationType]="TextRelationType.imageAlt"
            class="w-full"
            [id]="'ImageRightText'"
            [attr.scroll-mark]="headerForm.controls.rightImage.value.id"
            [formControl]="headerForm.controls.rightImage.controls.imageAlts"
            [label]="cmsTextProvider.header.altTextRight" />
          <aw-button
            [id]="'delete-right-image'"
            [type]="'button'"
            (click)="deleteImageRight()"
            [style]="'error'"
            class="w-12">
            <span class="material-symbols-rounded"> delete </span>
          </aw-button>
        </div>
      </div>
    </aw-section>
  </aw-section>
  <aw-preview [components]="[HeaderComponent]" [previewData]="previewData()" />
}
